import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import SizesMap, { defaultSize, sizes } from './Input.suggestions.sizes'
import { colors } from '../../../constants'

const Wrapper = styled.div`
    ${({ theme }) => {
        switch (theme) {
            case 'secondary':
                return `
          background: white;
          margin-top: 1px;
          font-size: 15px;
          padding: 18px 12px 17px;
          height: 35px;
          width: 100%;
        `
            case 'default':
            default:
                return ''
        }
    }}

    ${({ notRelative }) => !notRelative && `position: relative;`}

  ${({ size }) => {
        switch (size) {
            case SizesMap.sm:
                return 'width: 150px;'
            case SizesMap.md:
                return 'width: 300px;'
            case SizesMap.lg:
                return 'width: 400px;'
            case SizesMap.full:
            default:
                return 'width: 100%;'
        }
    }}
`

const Input = styled.input`
    background: ${colors.greylightest};
    ${({ borderColor }) => (borderColor ? `border: 1px solid ${borderColor};` : `border: 1px solid ${colors.greylight};`)}
    border-radius: 3px;
    outline: none;
    padding: 5px;
    width: 100%;

    &:active,
    &:focus,
    &:hover {
        border: 1px solid ${colors.grey};
    }

    ${({ error }) =>
        error &&
        `
    border: 1px solid ${colors.red};
  `}
`

const OptionsBoard = styled.ul`
    background: ${colors.white};
    box-shadow: -1px 2px 4px #0002;
    color: ${colors.black};
    margin: 0;
    opacity: 0;
    padding: 0;
    position: fixed;
    text-align: left;
    transition: all 0.3s;
    transform: scaleY(0);
    transform-origin: top;
    height: auto;
    width: auto;
    z-index: 2;

    ${({ isOpen }) =>
        isOpen &&
        `
      opacity: 1;
      transform: scaleY(1);
    `}
`

const Option = styled.li`
    cursor: pointer;
    display: block;
    padding: 5px 25px;

    &:hover {
        background: ${colors.greylightest};
        color: ${colors.bluedark};
    }
`

class InputSuggestions extends PureComponent {
    static propTypes = {
        autocomplete: PropTypes.bool,
        className: PropTypes.string,
        innerRef: PropTypes.shape({}),
        maxOptions: PropTypes.number,
        onChange: PropTypes.func,
        onTextChange: PropTypes.func,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                value: PropTypes.string,
            })
        ),
        placeholder: PropTypes.string,
        size: PropTypes.oneOf(sizes),
        value: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
        borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        notRelative: PropTypes.bool,
        theme: PropTypes.string,
    }

    static defaultProps = {
        autocomplete: true,
        className: '',
        innerRef: null,
        maxOptions: 5,
        onChange: () => null,
        onTextChange: () => null,
        options: [],
        placeholder: '',
        size: defaultSize,
        value: '',
        borderColor: null,
        notRelative: false,
        theme: 'default',
    }

    /**
     * Takes `props.value` as default `state.innerValue` if the first
     * is defined and they are both different.
     * @param {Object} nextProps
     * @param {Object} prevState
     */
    static getDerivedStateFromProps(nextProps, prevState) {
        const { value: valueProps } = nextProps
        const { lastPropsValue } = prevState

        if (typeof valueProps === 'string' && valueProps !== lastPropsValue) {
            return { innerValue: valueProps, lastPropsValue: valueProps }
        }

        return null
    }

    constructor(props) {
        super(props)

        this.state = {
            innerValue: '',
            lastPropsValue: '',
            isOpen: false,
        }

        this.onBlurInput = this.onBlurInput.bind(this)
        this.onChange = this.onChange.bind(this)
        this.onFocusInput = this.onFocusInput.bind(this)
        this.renderOption = this.renderOption.bind(this)
    }

    componentWillUnmount() {
        if (this.timeout) {
            clearTimeout(this.timeout)
        }
    }

    get optionsList() {
        const { maxOptions, options } = this.props
        return options.slice(0, maxOptions)
    }

    onBlurInput() {
        // The panel has be closed with a timeout because if not,
        // the option is not clicked sometimes
        this.timeout = setTimeout(() => this.setState({ isOpen: false }), 200)
    }

    onChange({ target: { value } }) {
        const { onTextChange } = this.props
        this.setState({ innerValue: value }, () => onTextChange(value))
    }

    onFocusInput() {
        this.setState({ isOpen: true })
    }

    onSelectOption(key) {
        const { onChange } = this.props
        this.setState({ isOpen: false, innerValue: '' }, () => onChange(key))
    }

    renderOption({ key, value: optionValue }) {
        return (
            <Option key={key} onClick={() => this.onSelectOption(key, optionValue)}>
                {optionValue}
            </Option>
        )
    }

    render() {
        const {
            autocomplete,
            innerRef,
            size,
            value,
            onChange,
            onTextChange,
            borderColor,
            notRelative,
            ...props
        } = this.props
        const { innerValue, isOpen } = this.state

        return (
            <Wrapper notRelative={notRelative} size={size}>
                <Input
                    type="text"
                    onBlur={this.onBlurInput}
                    onChange={this.onChange}
                    onFocus={this.onFocusInput}
                    ref={innerRef}
                    value={innerValue}
                    borderColor={borderColor}
                    {...props}
                    autoComplete={autocomplete ? 'on' : 'off'}
                />
                <OptionsBoard isOpen={isOpen}>{this.optionsList.map(this.renderOption)}</OptionsBoard>
            </Wrapper>
        )
    }
}

// eslint-disable-next-line react/no-multi-comp
export default React.forwardRef((props, ref) => <InputSuggestions innerRef={ref} {...props} />)
